"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { SignInButton, useAuth } from "@clerk/nextjs";
import { Check, Package, PackageX, Plus, Search } from "lucide-react";
import { useCardOwnershipStatus } from "modules/cards/hooks/useCardOwnershipStatus";
import { ListMembership as CardListMembership } from "modules/cards/model/cardStatus";
import { useCardModal } from "modules/cards/stores/useCardsStore";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { useSets } from "modules/common/hooks/useSets";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";
import { cn } from "utils/utils";
import CollectionIcon from "modules/common/components/Icons/CollectionIcon";

interface Props {
  dataId: string;
  cardName: string;
}

export function CardOwnershipRibbon({ dataId, cardName }: Props) {
  const { serializeBinderParams } = useFacetSearchParamsContract();
  const { isSignedIn, isLoaded } = useAuth();
  const { closeModal } = useCardModal();
  const { getSetName } = useSets();
  const { query } = useCardOwnershipStatus(dataId ? [dataId] : []);

  if (!isLoaded || query.isLoading) {
    return (
      <div className="alert alert-soft alert-neutral gap-2 text-sm">
        <div className="skeleton size-[18px] shrink-0 self-start rounded" />
        <div className="flex flex-col items-start gap-1.5">
          <div className="skeleton h-5 w-32 rounded-full" />
          <div className="skeleton h-5 w-28 rounded-full" />
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="alert alert-soft alert-info gap-2 text-sm">
        <span>Sign in to see your relationship with this card</span>
        <div className="shrink-0">
          <SignInButton mode="modal">
            <button className="btn btn-primary text-nowrap">Sign in</button>
          </SignInButton>
        </div>
      </div>
    );
  }

  const status = query.data?.[dataId];
  const exact = status?.exact ?? 0;
  const alternate = status?.alternate ?? [];
  const total = status?.total ?? 0;
  const lists = status?.lists ?? [];
  const wanted = lists.filter((l) => l.listType === TradeListType.WANTS);
  const haves = lists.filter((l) => l.listType === TradeListType.TRADES);
  const wantedQty = wanted.reduce((sum, l) => sum + (l.quantity ?? 0), 0);
  const havesQty = haves.reduce((sum, l) => sum + (l.quantity ?? 0), 0);

  if (total === 0 && lists.length === 0) {
    return (
      <div className="alert alert-soft text-sm">
        <PackageX size={ICON_SIZE} />
        <span>
          You don&apos;t own this card and it&apos;s not on any of your lists.
        </span>
      </div>
    );
  }

  const altSets = Array.from(
    new Map(
      alternate
        .filter((card) => card.setCode)
        .map((card) => [`${card.game}:${card.setCode}`, card])
    ).values()
  );
  const altSetNames = altSets.map((card) =>
    getSetName(card.setCode, card.game)
  );

  const collectionHref = `/x/binder${serializeBinderParams({
    section: "collection",
    query: cardName,
  })}`;

  return (
    <div className="alert alert-soft alert-neutral gap-2 text-sm">
      <CollectionIcon fontSize={ICON_SIZE} className="shrink-0 self-start" />
      <div className="flex flex-col items-start gap-1.5">
        {exact > 0 && (
          <Link
            href={collectionHref}
            className="badge badge-soft badge-success gap-1.5 whitespace-nowrap transition-colors hover:brightness-95"
            onClick={closeModal}
          >
            <Check size={12} />
            {exact} exact printing{exact === 1 ? "" : "s"}
          </Link>
        )}
        {alternate.length > 0 && (
          <div className={cn(altSetNames.length > 0 && "lg:tooltip")}>
            {altSetNames.length > 0 && (
              <div className="tooltip-content hidden flex-col text-xs lg:flex">
                {altSetNames.map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </div>
            )}
            <Link
              href={collectionHref}
              className="badge badge-soft badge-info gap-1.5 whitespace-nowrap transition-colors hover:brightness-95"
              onClick={closeModal}
            >
              <Plus size={12} />
              {alternate.length} in other printing
              {alternate.length === 1 ? "" : "s"}
            </Link>
          </div>
        )}
        {wanted.length > 0 && (
          <ListMembershipPill
            lists={wanted}
            icon={<Search size={12} />}
            label={`${wantedQty} wanted on ${wanted.length} list${wanted.length === 1 ? "" : "s"}`}
            variant="secondary"
            cardName={cardName}
          />
        )}
        {haves.length > 0 && (
          <ListMembershipPill
            lists={haves}
            icon={<Package size={12} />}
            label={`${havesQty} listed on ${haves.length} list${haves.length === 1 ? "" : "s"}`}
            variant="warning"
            cardName={cardName}
          />
        )}
      </div>
    </div>
  );
}

type PillVariant = "secondary" | "warning";

const VARIANT_CLASSES: Record<PillVariant, string> = {
  secondary: "badge-soft badge-secondary hover:brightness-95",
  warning: "badge-soft badge-warning hover:brightness-95",
};

interface ListMembershipPillProps {
  lists: CardListMembership[];
  icon: React.ReactNode;
  label: string;
  variant: PillVariant;
  cardName: string;
}

const MENU_WIDTH = 224;
const MENU_PADDING = 8;

function ListMembershipPill({
  lists,
  icon,
  label,
  variant,
  cardName,
}: ListMembershipPillProps) {
  const { serializeBinderParams } = useFacetSearchParamsContract();
  const { closeModal } = useCardModal();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [positioned, setPositioned] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const baseClasses = cn(
    "badge gap-1.5 whitespace-nowrap transition-colors",
    VARIANT_CLASSES[variant]
  );

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();

    let top = rect.bottom + 4;
    let left = rect.left;

    if (left + MENU_WIDTH > window.innerWidth - MENU_PADDING) {
      left = window.innerWidth - MENU_WIDTH - MENU_PADDING;
    }
    if (left < MENU_PADDING) left = MENU_PADDING;

    const menuHeight = menuRef.current?.offsetHeight ?? 200;
    if (top + menuHeight > window.innerHeight - MENU_PADDING) {
      top = rect.top - menuHeight - 4;
    }
    if (top < MENU_PADDING) top = MENU_PADDING;

    setMenuPos({ top, left });
    setPositioned(true);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    calculatePosition();

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
      setPositioned(false);
    };
    const reposition = () => calculatePosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, calculatePosition]);

  const buildListHref = (list: CardListMembership) =>
    `/x/binder${serializeBinderParams({
      section: list.listType === TradeListType.WANTS ? "wants" : "haves",
      listId: list.listId,
      query: cardName,
    })}`;

  if (lists.length === 1) {
    return (
      <Link
        href={buildListHref(lists[0])}
        className={baseClasses}
        onClick={closeModal}
      >
        {icon}
        {label}
      </Link>
    );
  }

  const handleButtonClick = () => {
    setIsOpen((v) => {
      if (v) setPositioned(false);
      return !v;
    });
  };

  const closeAndNavigate = () => {
    setIsOpen(false);
    setPositioned(false);
    closeModal();
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={cn(baseClasses, "cursor-pointer")}
        onClick={handleButtonClick}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {icon}
        {label}
      </button>
      {isOpen &&
        createPortal(
          <ul
            ref={menuRef}
            role="menu"
            className="menu bg-base-100 rounded-box border-base-content/20 fixed z-[9999] border p-1 shadow-lg"
            style={{
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              width: `${MENU_WIDTH}px`,
              opacity: positioned ? 1 : 0,
            }}
          >
            {lists.map((l) => (
              <li key={l.listId}>
                <Link
                  href={buildListHref(l)}
                  className="flex items-center justify-between gap-2 text-sm"
                  onClick={closeAndNavigate}
                >
                  <span className="truncate">{l.listName}</span>
                  {l.quantity > 1 && (
                    <span className="text-base-content/60 shrink-0">
                      ×{l.quantity}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>,
          // Portal into the closest open <dialog> if present — native
          // dialog.showModal() renders in the browser's top layer, which
          // sits above document.body, so a body-level portal would be
          // hidden behind the modal.
          buttonRef.current?.closest("dialog") ?? document.body
        )}
    </>
  );
}
